/// <amd-module name="RMWidgets/ReleasePipelineWidget/components/ReleasePipelineStatusWidget" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import {
    VssComponent,
    IVssComponentContext
} from "VSS/Platform/Layout";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Table, ITableColumn, SimpleTableCell, TableColumnStyle } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";


import { WidgetProps, LoadingStatus } from "Dashboards/WidgetContracts/WidgetProps";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";

import { VisualTitle } from "Reporting/Visuals/VisualTitle";
import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";

import { EnvironmentBox } from "RMWidgets/ReleasePipelineWidget/components/EnvironmentBox";
import { getEnvironmentStatusText } from "RMWidgets/ReleasePipelineWidget/ReleasePipelineStatusUtils";
import { ReleasePipelineStatusDataProvider } from "RMWidgets/ReleasePipelineWidget/ReleasePipelineStatusDataProvider";

import {
    Release,
    ReleaseEnvironment,
    ReleaseDefinitionEnvironment,
    ReleaseApproval,
    ApprovalStatus
} from "Release/Client/Contracts/Release";
import { getBorderColorCssPrefix } from "Release/EnvironmentNode/Helpers";
import { EnvironmentNode } from "Release/EnvironmentNode/EnvironmentNode";
import { ComputedDeploymentStatus } from "Release/EnvironmentNode/Types";
import {
    createReleaseViewUrl,
    createReleaseEnvironmentUrl,
    createReleaseDefinitionUrl
} from "Release/Helpers/Link";

import * as Resources from "RMWidgets/ReleasePipelineWidget/Resources";

export interface ReleasePipelineStatusWidgetState {
    environments: ReleaseDefinitionEnvironment[];
    releases: Release[];
    releaseDefinitionName: string;
    releaseDefinitionId: number;
}

export class ReleasePipelineStatusWidget extends VssComponent<WidgetProps, ReleasePipelineStatusWidgetState> {
    private static releaseNameListColumn = "release-name";
    private static releaseEnvironmentsListColumn = "release-environments";
    private dataProvider: ReleasePipelineStatusDataProvider;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            environments: [],
            releases: [],
            releaseDefinitionName: "",
            releaseDefinitionId: 0
        };
        this.dataProvider = new ReleasePipelineStatusDataProvider(context.pageContext);
    }

    componentDidMount() {
        if (this.props.settings) {
            this.getAndSetReleasePipelineData();
        }
    }

    componentDidUpdate(prevProps: WidgetProps) {
        if (this.props.settings && this.props.settings !== prevProps.settings) {
            this.getAndSetReleasePipelineData();
        }
    }

    async getAndSetReleasePipelineData() {
        try {
            const parsedSettings = JSON.parse(this.props.settings!);
            const releasePipelineStatusData = await this.dataProvider.getReleasePipelineStatusData(parsedSettings.releaseDefinitionId);

            this.setState({
                environments: releasePipelineStatusData.releaseDefinition.environments,
                releases: releasePipelineStatusData.releases,
                releaseDefinitionName: releasePipelineStatusData.releaseDefinition.name,
                releaseDefinitionId: releasePipelineStatusData.releaseDefinition.id
            });

            this.props.onWidgetLoaded();
        } catch (error) {
            this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
        }
        finally {
            WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
        }
    }

    private areSettingsValid(): boolean {
        const parsedSettings = JSON.parse(this.props.settings!);
        return parsedSettings &&
               parsedSettings.releaseDefinitionId &&
               parsedSettings.releaseDefinitionId > 0;
    }

    render(): JSX.Element {
        if (!this.props.settings || !this.areSettingsValid()) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        }

        return (
            <>
                <VisualTitle text={this.props.name} />
                <div className="rd-widget-container">
                    {this.renderEnvironmentsContainer()}
                    <hr className="widget-separator" />
                    {this.renderReleasesTableContainer()}
                    {this.renderFooterLink()}
                </div>
            </>
        );
    }

    /** 
     * 
     * Section that renders environment boxes - top-half of widget. 
     * 
     */

    private renderEnvironmentsContainer(): JSX.Element {
        // This widget has 4 sizes: 3x2 (width: 500px), 4x2 (width > 500px), 10x2 (width > 670px), 10x4 (width > 670px)
        let maxEnvironmentBoxes = 3; // widget is 3x2
        if (this.props.size.width > 670) { // widget is either 10x2 or 10x4
            maxEnvironmentBoxes = 12;
        }
        else if (this.props.size.width > 500) { // widget is 4x2
            maxEnvironmentBoxes = 4;
        }

        let renderMoreEnvironments = false;
        let countOfMoreEnvironments = 0;
        let { environments } = this.state;
        // Sort environments by rank.
        environments.sort((a, b) => { return a.rank - b.rank });

        if (environments.length > maxEnvironmentBoxes) {
            renderMoreEnvironments = true;
            countOfMoreEnvironments = environments.length - maxEnvironmentBoxes;
            environments = environments.slice(0, maxEnvironmentBoxes);
        }

        return (
            <div className='rd-widget-envs-container'>
                {this.renderEnvironmentBoxes(environments)}
                {
                    renderMoreEnvironments ?
                        this.renderAdditionalEnvironments(countOfMoreEnvironments) :
                        undefined
                }
            </div>
        );
    }

    private renderEnvironmentBoxes(environmentsToDisplay: ReleaseDefinitionEnvironment[]): JSX.Element {
        return (
            <>
                {
                    environmentsToDisplay.map(environment => {
                        const releaseEnvironment = this.getReleaseEnvironmentFromDefinitionEnvironment(environment);
                        return (
                            <div key={environment.id}>
                                <EnvironmentBox
                                    releaseEnvironment={releaseEnvironment}
                                    environmentName={environment.name}
                                    summaryUrl={createReleaseEnvironmentUrl(this.context.pageContext, environment.id, this.state.releaseDefinitionId)}
                                    releaseName={this.getReleaseName(environment.currentRelease.id)}
                                    releaseUrl={createReleaseViewUrl(this.context.pageContext, environment.currentRelease.id)}
                                    openLinksInNewTab={this.props.openLinksInNewTab}
                                />
                            </div>
                        );
                    })
                }
            </>
        )
    }

    /** 
     *  If there are more environments than the widget can display,
     *  hide the additional environments behind a '+' button.
     */
    private renderAdditionalEnvironments(countOfMoreEnvironments: number): JSX.Element {
        return <div className="rd-widget-nav-cell">
            <FPSLink
                target={this.props.openLinksInNewTab ? "_blank" : undefined}
                rel="noopener noreferrer"
                href={createReleaseDefinitionUrl(this.context.pageContext, this.state.releaseDefinitionId)}
            >
                <div className="more-env-box">{`+ ${countOfMoreEnvironments}`}</div>
            </FPSLink>
        </div>
    }

    /** 
     * 
     * Section that renders environment nodes for each release - bottom-half of widget. 
     * 
     */

    private renderReleasesTableContainer(): JSX.Element {
        let styleObj = {height: this.props.size.height > 330 ? "480px" : "150px"}
        return (
            <div className="releases-table-container" style={styleObj}>
                <Table
                    className={"releases-table"}
                    columns={this.getReleaseListColumns()}
                    showHeader={false}
                    showLines={false}
                    itemProvider={new ArrayItemProvider<Release>(this.state.releases)}
                />
            </div>
        );
    }

    private renderFooterLink(): JSX.Element {
        return (
            <div className="rd-widget-footer-link" tabIndex={0}>
                <FPSLink
                    target={this.props.openLinksInNewTab ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    href={createReleaseDefinitionUrl(this.context.pageContext, this.state.releaseDefinitionId)}
                >
                    <span>{format(Resources.RDWidgetViewSummaryLinkText, this.state.releaseDefinitionName)}</span>
                </FPSLink>
            </div>
        );
    }


    private getReleaseListColumns(): ITableColumn<Release>[] {
        return [
            {
                id: ReleasePipelineStatusWidget.releaseNameListColumn,
                name: ReleasePipelineStatusWidget.releaseNameListColumn,
                width: -20,
                columnStyle: TableColumnStyle.Primary,
                className: "rd-widget-table-name-column",
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release) => this.renderReleaseNameColumn(rowIndex, columnIndex, tableColumn, release)
            },
            {
                id: ReleasePipelineStatusWidget.releaseEnvironmentsListColumn,
                name: ReleasePipelineStatusWidget.releaseEnvironmentsListColumn,
                width: -80,
                columnStyle: TableColumnStyle.Secondary,
                className: "rd-widget-table-environments-column",
                renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element => this.renderEnvironmentNodesColumn(rowIndex, columnIndex, tableColumn, release)
            }
        ];
    }

    private renderReleaseNameColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={`col-${columnIndex}`}
            >
                <div className="text-wrap">
                    <FPSLink
                        className="release-name rd-widget-nav-cell"
                        href={createReleaseViewUrl(this.context.pageContext, release.id)}
                        target={this.props.openLinksInNewTab ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        tooltipProps={{
                            text: release.name,
                            className: "rm-rich-content-tooltip"
                        }}>
                        {release.name}
                    </FPSLink>
                </div>
            </SimpleTableCell>
        );
    }

    private renderEnvironmentNodesColumn(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Release>, release: Release): JSX.Element {
        // This widget has 4 sizes: 3x2 (width: 500px), 4x2 (width > 500px), 10x2 (width > 670px), 10x4 (width > 670px)
        let maxNodes = 4; // widget is 3x2
        if (this.props.size.width > 670) { // widget is either 10x2 or 10x4
            maxNodes = 15;
        }
        else if (this.props.size.width > 500) { // widget is 4x2
            maxNodes = 5;
        }

        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={`col-${columnIndex}`}
            >
                <div className="rd-widget-environment-nodes">
                    {
                        release.environments.map((environment: ReleaseEnvironment, index: number) => {
                            return (index < maxNodes) ? this.renderEnvironmentNode(environment) : undefined;
                        })
                    }
                    {
                        // If number of environments exceed the maximum number of nodes, display count of remaining environments.
                        release.environments.length > maxNodes ?
                            (
                                <div className="more-env-count">
                                    {`+${(release.environments.length - maxNodes).toLocaleString()}`}
                                </div>
                            ) :
                            undefined
                    }
                </div>
            </SimpleTableCell>
        );
    }

    private renderEnvironmentNode(environment: ReleaseEnvironment): JSX.Element {
        const pendingApprovals: ReleaseApproval[] = [];
        pendingApprovals.push(...(environment.preDeployApprovals || []).filter(app => app.status === ApprovalStatus.Pending));
        pendingApprovals.push(...(environment.postDeployApprovals || []).filter(app => app.status === ApprovalStatus.Pending))

        return (
            <div key={environment.id}>
                <EnvironmentNode
                    pendingApprovals={pendingApprovals}
                    nodeHeight={2}
                    nodeWidth={80}
                    nodeRightMargin={5}
                    statusText={getEnvironmentStatusText(environment.status)}
                    statusIndicator={getBorderColorCssPrefix(environment)}
                    computedStatus={ComputedDeploymentStatus.Undefined}
                    environment={environment}
                />
            </div>
        );
    }


    /* Get release name given the environment's current release definition id. */
    private getReleaseName(releaseId: number): string {
        if (this.state.releases) {
            const currentRelease = this.state.releases.find(release => releaseId === release.id);
            return currentRelease ? currentRelease.name : "";
        }

        return "";
    }

    private getReleaseEnvironmentFromDefinitionEnvironment(definitionEnvironment: ReleaseDefinitionEnvironment): ReleaseEnvironment | undefined {
        const releaseShallowReference = definitionEnvironment.currentRelease ? this.state.releases.find(r => r.id === definitionEnvironment.currentRelease.id) : undefined;
        return releaseShallowReference ? releaseShallowReference.environments.find(env => env.definitionEnvironmentId === definitionEnvironment.id) : undefined;
    }
}

VssComponent.register("releasePipelineStatusWidget", ReleasePipelineStatusWidget);
